import React, { useEffect, useState } from 'react'
import { Button, Cascader, Checkbox, Form, Input, Radio, Select } from 'antd'
import { useTranslation } from 'react-i18next'
import { fetchCrossDataSource, fetchCrossDataSourceTree, fetchDimensionByCrossData, fetchDimensionByIndicator, fetchDimensionBySource, fetchDimensionDataSource, fetchIndicatorByDimension } from '../../apis/indicator'
import { FormButton, FormulaWrapper } from './indicator.styled'
import { QueryExpression } from '../../constants/DashboardConf'
import Filter from '../DashboardSetting/components/Filter'
import dayjs from 'dayjs'

const { COUNT, AVG, SUM } = QueryExpression
function IndicatorForm (props) {
  const { params, modalData, defConfig, defPlaceholder, brands, form, handleModalClose, loading, dataSource, setDataSource, counter, setCounter, tableMapping, tableColMapping, allTableKeyMapping, crossDataColumn, setCrossDataColumn } = props
  const { t: ln_t } = useTranslation()
  const { fetchParams } = params
  const formVal = Form.useWatch() || {}
  const [crossDataTable, setCrossDataTable] = useState([])
  const [dimensionBySrc, setDimensionBySrc] = useState([])
  const [baseIndicator, setBaseIndicator] = useState([])
  const [allIndicator, setAllIndicator] = useState([])
  const isCrossData = formVal.dataSourceId === 'crossData'
  const columnOpt = tableColMapping[`${formVal.dataSourceId}-${formVal.tableName}`]?.map(i => ({ value: i.columnName, label: i.columnName, ...i }))
  const columnMapping = columnOpt?.reduce((all, i) => ({ ...all, [i.columnName]: i }), {}) || {}

  // ----- counter -----
  const handleDelCounter = key => {
    setCounter(prev => prev.filter(i => i !== key))
    form.setFieldsValue({ [`${key}-column`]: undefined, [`${key}-value`]: undefined, [`${key}-operator`]: undefined })
  }
  const handleAddCounter = () => setCounter(prev => [...prev, counter.length ? Math.max(...counter) + 1 : 1])

  // ----- fetch API -----
  const getDataSource = async (brandId = fetchParams?.brandId) => {
    const res = await fetchDimensionDataSource(brandId)
    if (res?.status === 200) setDataSource(res?.data?.filter(i => i.dataSourceId))
  }

  const getCrossDataSource = async brandId => {
    const crossData = await fetchCrossDataSource(brandId)
    if (crossData?.status === 200) setCrossDataTable(crossData?.data?.filter(i => i.id)?.map(i => ({ value: i.id, label: i.name })))
  }

  const getDimensionBySrc = async (dataSrc, tableName) => {
    const res = await fetchDimensionBySource(dataSrc, tableName)
    if (res?.status === 200) setDimensionBySrc(res?.data?.map(i => ({ value: i.id, label: i.name })))
  }

  const getDimensionByIndicator = async (indicator1, indicator2, useBase = formVal.useBase) => {
    const res = await fetchDimensionByIndicator(indicator1, useBase ? 0 : indicator2)
    if (res?.status === 200) setDimensionBySrc(res?.data?.map(i => ({ value: i.id, label: i.name })))
  }

  const getIndicators = async (brandId = formVal?.brandId, isMonthly = formVal.isMonthly) => {
    if (!brandId) return
    const extras = { isMonthly: isMonthly ? 'yes' : 'no' }
    const base = await fetchIndicatorByDimension(brandId, { scope: 'basic', ...extras })
    if (base?.status === 200) setBaseIndicator(base?.data?.map(i => ({ value: i.id, label: i.name })))
    const all = await fetchIndicatorByDimension(brandId, extras)
    if (all?.status === 200) setAllIndicator(all?.data?.map(i => ({ value: i.id, label: i.name })))
  }

  const getDimensionByCrossData = async id => {
    const res = await fetchDimensionByCrossData(id)
    if (res?.status === 200) setDimensionBySrc(res?.data?.map(i => ({ value: i.id, label: i.name })))
  }

  const getCrossDataTree = async id => {
    const res = await fetchCrossDataSourceTree(id, { type: 2 })
    if (res?.status !== 200) return
    const crossData = res?.data?.filter(i => i.dataSourceId)?.map(i => ({
      value: i?.dataSourceId,
      label: i?.dataSourceName,
      dataSourceType: i?.dataSourceType,
      disableCheckbox: true,
      children: i.tables?.map(table => ({
        value: table?.tableName,
        label: table?.tableName,
        disableCheckbox: true,
        children: table?.columns?.map(col => ({
          ...col,
          value: col?.columnName,
          label: col?.alias || col?.columnName
        }))
      }))
    }))
    setCrossDataColumn(crossData)
  }

  // ----- on form field change -----
  const ruleFields = ['isMonthly', 'dataSourceId', 'tableName', 'columnName', 'aggregate', 'dimensions']
  const calculationFields = ['isMonthly', 'indicator1', 'operator', 'indicator2', 'useBase', 'dimensions']
  const resetAllFilterFields = () => counter?.reduce((all, i) => ({ ...all, [`${i}-column`]: undefined, [`${i}-value`]: undefined, [`${i}-operator`]: undefined }), {})

  const handleChangeMethod = e => {
    const formFields = e?.target?.value === 'rule' ? ruleFields : calculationFields
    const fieldsObj = formFields?.reduce((all, i) => ({ ...all, [i]: undefined }), {})
    form.setFieldsValue({ ...fieldsObj, ...resetAllFilterFields() })
    setCounter([])
  }

  const handleChangeMonthly = (e, reset = true) => {
    if (formVal.processingMethod === 'rule') return
    const fieldsObj = calculationFields?.filter(i => i !== 'isMonthly')?.reduce((all, i) => ({ ...all, [i]: undefined }), {})
    if (reset) form.setFieldsValue(fieldsObj)
    getIndicators(formVal?.brandId, e?.target?.checked)
  }

  const handleChangeBrand = (e, reset = true, isMonthly) => {
    if (reset) form.setFieldsValue({ dataSourceId: undefined, tableName: undefined, columnName: undefined, dimensions: undefined, ...resetAllFilterFields() })
    if (!e) return
    getDataSource(e)
    getCrossDataSource(e)
    getIndicators(e, isMonthly)
  }

  const handleChangeDataSource = () => form.setFieldsValue({ tableName: undefined, columnName: undefined, dimensions: undefined, ...resetAllFilterFields() })

  const handleChangeTable = e => {
    if (isCrossData) {
      getCrossDataTree(e)
      getDimensionByCrossData(e)
    } else getDimensionBySrc(formVal.dataSourceId, e)
    form.setFieldsValue({ columnName: undefined, dimensions: undefined, ...resetAllFilterFields() })
  }

  const handleChangeIndicator = (indicator1, indicator2) => {
    if (!indicator1 || !indicator2) return
    getDimensionByIndicator(indicator1, indicator2)
  }

  // ----- on modal data change -----
  useEffect(() => {
    if (!modalData?.open) return
    handleChangeBrand(modalData?.data?.brandId, false, modalData?.data?.isMonthly === 'yes')
    if (!modalData?.data?.id) return
    const { counter = [], formSetting, dateFilter } = modalData?.data?.UISetting
    const { processingMethod, dataSourceId, tableName, indicator1, indicator2, ...other } = formSetting || {}
    setCounter(counter)
    const formattingDateFilter = {}
    counter?.forEach(i => { if (dateFilter[i]) formattingDateFilter[`${i}-value`] = dayjs(other[`${i}-value`]) })
    form.setFieldsValue({ ...formSetting, ...formattingDateFilter })
    if (dataSourceId === 'crossData') {
      getCrossDataTree(tableName)
      getDimensionByCrossData(tableName)
    } else processingMethod === 'rule' ? getDimensionBySrc(dataSourceId, tableName) : getDimensionByIndicator(indicator1, indicator2)
  }, [modalData?.open])

  return <>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '24px 0 16px' }}>
      <div style={{ fontSize: '14px', fontWeight: 500 }}>{ln_t('util.brand')} :</div>
      <Form.Item {...defConfig('brandId', 'input')} label="" initialValue={fetchParams?.brandId || undefined} style={{ marginBottom: 0 }}>
        <Select
          showSearch
          placeholder={defPlaceholder('brandId', 'select')}
          optionFilterProp="children"
          options={brands}
          style={{ minWidth: '120px' }}
          onChange={handleChangeBrand}
        />
      </Form.Item>
    </div>
    <Form.Item {...defConfig('name', 'input')}>
      <Input placeholder={defPlaceholder('name', 'input')} />
    </Form.Item>
    <Form.Item {...defConfig('processingMethod', 'select')} initialValue="rule">
      <Radio.Group
        options={[{ value: 'rule', label: ln_t('indicator.ruleDefinition') }, { value: 'calculation', label: ln_t('indicator.calculation') }]}
        optionType="button"
        buttonStyle="solid"
        onChange={handleChangeMethod}
      />
    </Form.Item>
    <Form.Item {...defConfig('status', 'select')} initialValue={1}>
      <Radio.Group
        options={[{ value: 1, label: ln_t('util.enable') }, { value: 0, label: ln_t('util.disable') }]}
        optionType="button"
        buttonStyle="solid"
      />
    </Form.Item>
    <Form.Item {...defConfig('isMonthly', 'select', false)} noStyle valuePropName="checked">
      <Checkbox style={{ marginBottom: '10px' }} onChange={handleChangeMonthly}>{ln_t('indicator.calculateByMonth')}</Checkbox>
    </Form.Item>
    {formVal.processingMethod === 'rule'
      ? <>
        <Form.Item {...defConfig('dataSourceId', 'input')} >
          <Select
            showSearch
            placeholder={defPlaceholder('dataSourceId', 'select')}
            optionFilterProp="children"
            options={[...dataSource?.map(i => ({ value: i.dataSourceId, label: i.dataSourceName })), { value: 'crossData', label: ln_t('indicator.crossDataSource') }]}
            disabled={!formVal.brandId || modalData?.viewOnly}
            onChange={handleChangeDataSource}
          />
        </Form.Item>
        <Form.Item {...defConfig('tableName', 'input')} >
          <Select
            showSearch
            placeholder={defPlaceholder('tableName', 'select')}
            optionFilterProp="children"
            options={isCrossData ? crossDataTable : tableMapping[formVal.dataSourceId]?.map(i => ({ value: i.tableName, label: i.tableName }))}
            disabled={!formVal.dataSourceId || modalData?.viewOnly}
            onChange={handleChangeTable}
          />
        </Form.Item>
        <Form.Item {...defConfig('columnName', 'input')} label={ln_t('indicator.calculatedColumn')}>
          {isCrossData
            ? <Cascader
                expandTrigger="hover"
                placeholder={defPlaceholder('columnName', 'select')}
                options={crossDataColumn}
                disabled={!formVal.tableName || modalData?.viewOnly}
              />
            : <Select
              showSearch
              placeholder={defPlaceholder('columnName', 'select')}
              optionFilterProp="children"
              options={columnOpt}
              disabled={!formVal.tableName || modalData?.viewOnly}
            />}
        </Form.Item>
        <Form.Item {...defConfig('aggregate', 'select')}>
          <Select
            showSearch
            placeholder={defPlaceholder('aggregate', 'select')}
            options={[{ value: SUM, label: ln_t('dashboard.sum') }, { value: AVG, label: ln_t('dashboard.avg') }, { value: COUNT, label: ln_t('dashboard.count') }]}
            optionFilterProp="children"
          />
        </Form.Item>
        <Form.Item {...defConfig('dimension', 'select')} name="dimensions">
          <Select
            showSearch
            mode="multiple"
            placeholder={defPlaceholder('dimension', 'select')}
            options={[{ value: 0, label: ln_t('dashboard.basicDimension') }, ...dimensionBySrc || []]}
            optionFilterProp="children"
            disabled={!formVal.tableName || modalData?.viewOnly}
          />
        </Form.Item>
        <Filter
          {...props}
          counter={counter}
          filterOpt={isCrossData ? crossDataColumn : columnOpt}
          handleAddCounter={handleAddCounter}
          handleDelCounter={handleDelCounter}
          columnMapping={isCrossData ? allTableKeyMapping : columnMapping}
          disableActions={modalData?.viewOnly}
          viewOnly={modalData?.viewOnly}
          crossData={isCrossData}
        />
      </>
      : <>
        <div>{ln_t('indicator.calculationFormula')}</div>
        <FormulaWrapper>
          <Form.Item {...defConfig('indicator1', 'select')} label="">
            <Select
              showSearch
              options={allIndicator?.map(i => ({ ...i, disabled: i.value === formVal.indicator2 }))}
              optionFilterProp="children"
              disabled={!formVal.brandId || modalData?.viewOnly}
              onChange={e => handleChangeIndicator(e, formVal.indicator2)}
            />
          </Form.Item>
          <Form.Item {...defConfig('operator', 'select')} label="">
            <Select
              options={['+', '-', 'X', '/'].map(i => ({ value: i, label: i }))}
              disabled={!formVal.brandId || modalData?.viewOnly}
              style={{ width: '90px' }} />
          </Form.Item>
          <Form.Item {...defConfig('indicator2', 'select')} label="" style={{ marginBottom: 0 }}>
            <Select
              showSearch
              options={formVal.useBase ? baseIndicator : allIndicator?.map(i => ({ ...i, disabled: i.value === formVal.indicator1 }))}
              optionFilterProp="children"
              disabled={!formVal.brandId || modalData?.viewOnly}
              onChange={e => handleChangeIndicator(formVal.indicator1, e)}
            />
          </Form.Item>
          {formVal?.operator === '/' && <Form.Item {...defConfig('useBase', 'select', false)} noStyle valuePropName="checked">
            <Checkbox style={{ marginTop: '10px' }} onChange={() => form.setFieldsValue({ indicator2: undefined, dimensions: undefined })}>{ln_t('indicator.useBase')}</Checkbox>
          </Form.Item>}
        </FormulaWrapper>
        <Form.Item {...defConfig('dimension', 'select')} name="dimensions">
          <Select
            showSearch
            mode="multiple"
            placeholder={defPlaceholder('dimension', 'select')}
            options={dimensionBySrc}
            optionFilterProp="children"
            disabled={!formVal.indicator1 || !formVal.indicator2 || modalData?.viewOnly}
          />
        </Form.Item>
      </>}

    {!modalData?.viewOnly && <FormButton>
      <Button type="default" onClick={handleModalClose}>{ln_t('util.cancel')}</Button>
      <Button type="primary" htmlType="submit" loading={loading}>{ln_t('util.confirm')}</Button>
    </FormButton>}
  </>
}

export default IndicatorForm
